import React, { ComponentType, ElementType, useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  Icon,
  IconProps,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { CheckCircle2, Link2, MessagesSquare, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { FaInstagram } from 'react-icons/fa';
import { getInstagramUserProfile } from '../../../hooks/instagram';
import { getCurentOrgDataFromStorage } from '../../../services/organization';
import { Organization } from '../../../utils/types';
import { LogInViaInstagramOnboardingButton } from '../components/LogInViaInstagramOnboardingButton';

const setupSteps = [
  {
    title: 'Prepare account',
    description:
      'Use an Instagram business or professional account.',
    icon: CheckCircle2,
  },
  {
    title: 'Approve access',
    description:
      'Approve Instagram access for messages and comments.',
    icon: ShieldCheck,
  },
  {
    title: 'Start syncing',
    description:
      'Your inbox becomes ready for replies.',
    icon: MessagesSquare,
  },
];

export const InstagramOnboarding: React.FC = () => {
  const [org, setOrg] = useState<Organization>();
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const loadOrg = async () => {
      const response = await getCurentOrgDataFromStorage();
      if (response.success && response.data && typeof response.data === 'object') {
        setOrg(response.data);
      }
      setLoading(false);
    };

    loadOrg();
  }, []);

  useEffect(() => {
    const checkConnection = async () => {
      if (!org?.id) return;
      const response = await getInstagramUserProfile(org.id);
      if (response.success && Array.isArray(response.data) && response.data.length > 0) {
        navigate(`/organization/${org.id}/inbox/instagram/conversations`);
      }
    };

    checkConnection();
  }, [org, navigate]);

  if (loading) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg="#F7F4F8">
        <Spinner size="xl" color="#C13584" />
      </Flex>
    );
  }

  return (
    <Box minH="100vh" bg="#F7F4F8" px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
      <Grid
        maxW="1280px"
        mx="auto"
        templateColumns={{ base: '1fr', xl: '1.15fr 0.85fr' }}
        gap={6}
      >
        <Box
          bg="white"
          borderRadius="32px"
          border="1px solid"
          borderColor="rgba(15, 23, 42, 0.08)"
          boxShadow="0 24px 64px rgba(15,23,42,0.08)"
          px={{ base: 6, md: 10 }}
          py={{ base: 8, md: 10 }}
        >
          <VStack align="stretch" spacing={8}>
            <VStack align="flex-start" spacing={4}>
              <Badge colorScheme="pink" borderRadius="full" px={3} py={1}>
                Instagram setup
              </Badge>
              <Flex
                boxSize="72px"
                borderRadius="24px"
                bg="#FCE7F3"
                color="#C13584"
                align="center"
                justify="center"
              >
                <Icon as={FaInstagram as ComponentType<IconProps>} boxSize={10} />
              </Flex>
              <VStack align="flex-start" spacing={2}>
                <Heading size="2xl" color="gray.900">
                  Connect Instagram with confidence
                </Heading>
                <Text color="gray.500" lineHeight="1.8" maxW="560px">
                  Bring Instagram messages and comments into your CRM.
                </Text>
              </VStack>
            </VStack>

            <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
              {setupSteps.map((step, index) => (
                <Box
                  key={step.title}
                  border="1px solid"
                  borderColor="rgba(15, 23, 42, 0.08)"
                  borderRadius="24px"
                  p={5}
                  bg={index === 1 ? '#FCF2F8' : 'gray.50'}
                >
                  <Flex
                    boxSize="40px"
                    borderRadius="14px"
                    bg="white"
                    color="#C13584"
                    align="center"
                    justify="center"
                    mb={4}
                  >
                    <Icon as={step.icon as ElementType} boxSize={5} />
                  </Flex>
                  <Text fontWeight="700" color="gray.900" mb={2}>
                    {step.title}
                  </Text>
                  <Text fontSize="sm" color="gray.500" lineHeight="1.7">
                    {step.description}
                  </Text>
                </Box>
              ))}
            </Grid>

            <Box
              border="1px solid"
              borderColor="rgba(15, 23, 42, 0.08)"
              borderRadius="24px"
              p={6}
              bg="gray.50"
            >
              <VStack align="stretch" spacing={3}>
                <HStackWrapper label="What this setup enables">
                  Instagram messages in your CRM inbox
                </HStackWrapper>
                <HStackWrapper label="Permissions in use">
                  Messages, comments, and business account access
                </HStackWrapper>
                <HStackWrapper label="Current account rule">
                  One Instagram account per organization
                </HStackWrapper>
              </VStack>
            </Box>
          </VStack>
        </Box>

        <Box
          bg="white"
          borderRadius="32px"
          border="1px solid"
          borderColor="rgba(15, 23, 42, 0.08)"
          boxShadow="0 24px 64px rgba(15,23,42,0.08)"
          p={{ base: 6, md: 8 }}
          h="fit-content"
        >
          <VStack align="stretch" spacing={6}>
            <VStack align="flex-start" spacing={2}>
              <Badge colorScheme="purple" borderRadius="full" px={3} py={1}>
                Step 1 of 1
              </Badge>
              <Heading size="lg" color="gray.900">
                Start secure connection
              </Heading>
              <Text fontSize="sm" color="gray.500" lineHeight="1.8">
                Continue to Instagram, approve access, and return here.
              </Text>
            </VStack>

            <Box
              borderRadius="24px"
              bg="#FCF2F8"
              border="1px solid"
              borderColor="#F7C7DD"
              p={5}
            >
              <VStack align="stretch" spacing={3}>
                <HStackWrapper label="Connected organization">
                  {org?.name || 'Current organization'}
                </HStackWrapper>
                <HStackWrapper label="Trust note">
                  Authentication happens with Instagram.
                </HStackWrapper>
              </VStack>
            </Box>

            <LogInViaInstagramOnboardingButton orgId={org?.id} />

            <Button
              variant="ghost"
              borderRadius="full"
              leftIcon={<Link2 size={16} />}
              onClick={() => navigate('/')}
            >
              Return to dashboard
            </Button>
          </VStack>
        </Box>
      </Grid>
    </Box>
  );
};

const HStackWrapper = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <Box>
    <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.08em" color="gray.500" mb={1}>
      {label}
    </Text>
    <Text fontSize="sm" color="gray.800" lineHeight="1.7">
      {children}
    </Text>
  </Box>
);
